import { Params } from '@angular/router';
import { IimPagination } from '@iim-angular/components';

/**
 * Class representing pagination information for invoice lists.
 * Handles pagination state and conversion between route parameters and pagination objects.
 */
export class ListInvoicePagination implements IimPagination {
    private static readonly PAGE = 'page';
    public static readonly ELEMENTS_PER_PAGE = 'elementsPerPage';

    readonly elementsPerPage: number;
    readonly page: number;
    readonly totalEntries: number;
    readonly totalPages: number;

    /**
     * Creates a new pagination instance, either from an existing pagination object or with default values.
     * @param pagination Optional existing pagination object to initialize from
     */
    constructor(pagination?: IimPagination) {
        if (pagination) {
            this.page = pagination.page;
            this.elementsPerPage = pagination.elementsPerPage;
            this.totalEntries = pagination.totalEntries;
            this.totalPages = pagination.totalPages;
        } else {
            this.page = 1;
            this.elementsPerPage = 50;
            this.totalEntries = 0;
            this.totalPages = 0;
        }
    }

    /**
     * Creates a pagination object from route parameters.
     * @param params Route parameters containing pagination information
     * @returns A new ListInvoicePagination instance
     */
    public static fromRouteParams(params: Params): ListInvoicePagination {
        const defaultPagination = new ListInvoicePagination();
        let page = defaultPagination.page;
        let elementsPerPage = defaultPagination.elementsPerPage;

        if (params[ListInvoicePagination.PAGE]) {
            page = +params[ListInvoicePagination.PAGE];
        }
        if (params[ListInvoicePagination.ELEMENTS_PER_PAGE]) {
            elementsPerPage = +params[ListInvoicePagination.ELEMENTS_PER_PAGE];
        }

        return new ListInvoicePagination({
            page,
            elementsPerPage,
            totalEntries: defaultPagination.totalEntries,
            totalPages: defaultPagination.totalPages,
        });
    }

    /**
     * Converts the pagination object to route parameters.
     * Only includes non-default values to keep URLs clean.
     * @returns Route parameters object
     */
    public toRouteParams(): Params {
        const params: Params = {};

        if (this.page !== 1) {
            params[ListInvoicePagination.PAGE] = this.page;
        }
        if (this.elementsPerPage !== 50) {
            params[ListInvoicePagination.ELEMENTS_PER_PAGE] = this.elementsPerPage;
        }

        return params;
    }

    /**
     * Creates a new pagination object with the specified page number.
     * @param page The new page number
     * @returns A new ListInvoicePagination instance
     */
    public withPage(page: number): ListInvoicePagination {
        return new ListInvoicePagination({
            ...this,
            page,
        });
    }

    /**
     * Creates a new pagination object with the specified elements per page.
     * @param elementsPerPage The new elements per page value
     * @returns A new ListInvoicePagination instance
     */
    public withElementsPerPage(elementsPerPage: number): ListInvoicePagination {
        return new ListInvoicePagination({
            ...this,
            elementsPerPage,
        });
    }

    /**
     * Applies a pagination object to create a new instance.
     * @param pagination The pagination object to apply
     * @returns A new ListInvoicePagination instance
     */
    public apply(pagination: IimPagination): ListInvoicePagination {
        return new ListInvoicePagination(pagination);
    }

    /**
     * Converts the pagination object to a DTO.
     * @returns An IimPagination object
     */
    public toDto(): IimPagination {
        return {
            page: this.page,
            elementsPerPage: this.elementsPerPage,
            totalEntries: this.totalEntries,
            totalPages: this.totalPages,
        };
    }
}